import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import api from "../lib/api";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { CHART_TOOLTIP_STYLE } from "../config/uiHelpers";

const Analytics = ({ user, logout }) => {
  const [analytics, setAnalytics] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [analyticsRes, campaignsRes, leadsRes] = await Promise.all([
        axios.get(`${API}/analytics`),
        axios.get(`${API}/campaigns`),
        api.get(`${API}/leads`)
      ]);
      setAnalytics(analyticsRes.data);
      setCampaigns(campaignsRes.data);
      setLeads(leadsRes.data);
    } catch (error) {
      console.error("Failed to fetch analytics");
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);

  const campaignData = [
    { name: 'Draft', value: campaigns.filter(c => c.status === 'draft').length },
    { name: 'Active', value: campaigns.filter(c => c.status === 'active').length },
    { name: 'Completed', value: campaigns.filter(c => c.status === 'completed').length }
  ];

  const leadStatusData = [
    { name: 'New', value: leads.filter(l => l.status === 'new').length },
    { name: 'Contacted', value: leads.filter(l => l.status === 'contacted').length },
    { name: 'Qualified', value: leads.filter(l => l.status === 'qualified').length },
    { name: 'Converted', value: leads.filter(l => l.status === 'converted').length }
  ];

  const COLORS = ['#002FA7', '#10B981', '#F59E0B', '#DC2626'];

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      
      <div className="flex-1 ml-64">
        <div className="p-8">
          <div className="mb-8">
            <h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-bold mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>
              Analytics
            </h1>
            <p className="text-base leading-relaxed text-slate-700">
              Track your marketing performance and metrics
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="loading-spinner"></div>
            </div>
          ) : (
            <>
              <div data-testid="analytics-summary" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-white border border-slate-200 rounded-none shadow-sm p-6">
                  <div className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Total Products</div>
                  <div className="text-4xl font-bold text-[#002FA7]" style={{fontFamily: 'Outfit, sans-serif'}}>
                    {analytics?.total_products || 0}
                  </div>
                </div>
                <div className="bg-white border border-slate-200 rounded-none shadow-sm p-6">
                  <div className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Total Campaigns</div>
                  <div className="text-4xl font-bold text-[#10B981]" style={{fontFamily: 'Outfit, sans-serif'}}>
                    {analytics?.total_campaigns || 0}
                  </div>
                </div>
                <div className="bg-white border border-slate-200 rounded-none shadow-sm p-6">
                  <div className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Total Leads</div>
                  <div className="text-4xl font-bold text-[#F59E0B]" style={{fontFamily: 'Outfit, sans-serif'}}>
                    {analytics?.total_leads || 0}
                  </div>
                </div>
                <div className="bg-white border border-slate-200 rounded-none shadow-sm p-6">
                  <div className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Conversion Rate</div>
                  <div className="text-4xl font-bold text-[#DC2626]" style={{fontFamily: 'Outfit, sans-serif'}}>
                    {analytics?.conversion_rate || 0}%
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div data-testid="campaign-status-chart" className="bg-white border border-slate-200 rounded-none shadow-sm p-6">
                  <h3 className="text-xl sm:text-2xl tracking-tight leading-tight font-semibold mb-6" style={{fontFamily: 'Outfit, sans-serif'}}>
                    Campaign Status
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={campaignData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                      <XAxis dataKey="name" stroke="#475569" />
                      <YAxis stroke="#475569" />
                      <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                      <Bar dataKey="value" fill="#002FA7" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div data-testid="lead-status-chart" className="bg-white border border-slate-200 rounded-none shadow-sm p-6">
                  <h3 className="text-xl sm:text-2xl tracking-tight leading-tight font-semibold mb-6" style={{fontFamily: 'Outfit, sans-serif'}}>
                    Lead Status Distribution
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={leadStatusData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {leadStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Analytics;
